/**
 * Airport Autocomplete Hook
 *
 * Provides debounced airport search functionality for autocomplete dropdowns
 */

import { useState, useEffect, useRef } from 'react';
import { useLazyAutocompleteAirportsQuery } from '../services/redux/api/airport.api';
import type { AirportAutocomplete } from '../services/redux/api/airport.api';

interface UseAirportAutocompleteOptions {
  delay?: number;
  minQueryLength?: number;
  limit?: number;
}

interface UseAirportAutocompleteReturn {
  airports: AirportAutocomplete[];
  loading: boolean;
  error: string | null;
  search: (query: string) => void;
}

/**
 * Custom hook for airport autocomplete with debounced search
 *
 * @param options - Configuration options
 * @param options.delay - Debounce delay in milliseconds (default: 300ms)
 * @param options.minQueryLength - Minimum query length to trigger search (default: 1)
 * @param options.limit - Maximum number of results (default: 10)
 *
 * @example
 * ```tsx
 * const { airports, loading, error, search } = useAirportAutocomplete();
 *
 * const handleInputChange = (value: string) => {
 *   search(value);
 * };
 * ```
 */
export function useAirportAutocomplete(
  options: UseAirportAutocompleteOptions = {}
): UseAirportAutocompleteReturn {
  const { delay = 300, minQueryLength = 1, limit = 10 } = options;

  const [airports, setAirports] = useState<AirportAutocomplete[]>([]);
  const debounceTimerRef = useRef<NodeJS.Timeout | null>(null);

  const [trigger, { data, isLoading, error: apiError }] = useLazyAutocompleteAirportsQuery();

  // Debounced search function
  const search = (searchQuery: string) => {
    // Clear previous timer
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }

    // Don't search if query is too short
    if (!searchQuery || searchQuery.length < minQueryLength) {
      setAirports([]);
      return;
    }

    // Debounce the API call
    debounceTimerRef.current = setTimeout(() => {
      trigger({ query: searchQuery, limit });
    }, delay);
  };

  // Update airports when data changes
  useEffect(() => {
    if (data) {
      setAirports(data);
    }
  }, [data]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
    };
  }, []);

  return {
    airports,
    loading: isLoading,
    error: apiError ? 'Failed to fetch airports' : null,
    search,
  };
}
